import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AssetsResponse,
  InvitePartnerMemberInput,
  InvitePartnerMemberResult,
  PartnersResponse,
  ProfileResponse,
  ReferResponse,
  TeamResponse,
} from '@sparkery/contracts';
import { apiGet, apiPost } from './api.ts';

/**
 * The queries behind Refer, Assets, Team, Profile and Partners.
 *
 * None takes a partner argument: the server decides scope from the session, so a hook
 * that accepted one would imply a choice the client does not have.
 */
/**
 * `enabled: false` is for the staff dashboard, which shows the Refer panel only for a
 * partner session. /refer needs a partner and answers 400 without one, so the query has
 * to be held rather than allowed to fail.
 */
export function useRefer(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['refer'],
    queryFn: ({ signal }) => apiGet<ReferResponse>('/refer', signal),
    enabled: options.enabled ?? true,
  });
}

export function useAssets() {
  return useQuery({
    queryKey: ['assets'],
    queryFn: ({ signal }) => apiGet<AssetsResponse>('/assets', signal),
  });
}

export function useTeam() {
  return useQuery({
    queryKey: ['team'],
    queryFn: ({ signal }) => apiGet<TeamResponse>('/team', signal),
  });
}

export function useInviteTeamMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: InvitePartnerMemberInput) =>
      apiPost<InvitePartnerMemberResult>('/team/invitations', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['team'] });
    },
  });
}

export function usePartnerTeam(partnerId: string) {
  return useQuery({
    queryKey: ['partner-team', partnerId],
    queryFn: ({ signal }) => apiGet<TeamResponse>(`/partners/${partnerId}/team`, signal),
  });
}

export function useProfile() {
  return useQuery({
    queryKey: ['profile'],
    queryFn: ({ signal }) => apiGet<ProfileResponse>('/profile', signal),
  });
}

/**
 * One partner's links and codes, for the admin detail screen.
 *
 * The same shape Refer returns, because it is the same read — only scoped by an admin
 * naming a partner rather than by the session.
 */
export function usePartnerDetail(partnerId: string) {
  return useQuery({
    queryKey: ['partner-detail', partnerId],
    queryFn: ({ signal }) => apiGet<ReferResponse>(`/partners/${partnerId}`, signal),
  });
}

export function usePartners() {
  return useQuery({
    queryKey: ['partners'],
    queryFn: ({ signal }) => apiGet<PartnersResponse>('/partners', signal),
  });
}

/**
 * Bytes as a human size.
 *
 * Base 1024 with the familiar KB/MB labels — what a file manager shows, rather than
 * the technically-correct KiB nobody outside this file would recognise.
 */
export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

/** "1 Jul 2026". Fixed to UTC so a late-evening viewer does not see yesterday. */
export function shortDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** "1 Jul – 31 Dec 26", or an open-ended phrasing when one end is missing. */
export function dateWindow(startsAt: string | null, endsAt: string | null): string {
  const day = (iso: string) =>
    new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });

  if (startsAt && endsAt) return `${day(startsAt)} – ${day(endsAt)}`;
  if (startsAt) return `${day(startsAt)} onwards`;
  if (endsAt) return `until ${day(endsAt)}`;
  return 'No end date';
}
